import { Option, RecentSearch } from '../types';

export const RECENT_SEARCHES_KEY = 'recentSearches';
export const MAX_RECENT_SEARCHES = 5;

// An in-memory Storage used when the real Web Storage is unavailable (e.g.
// storage disabled, Safari private mode) so callers never crash on access.
const createNoopStorage = (): Storage => {
  const store = new Map<string, string>();
  return {
    getItem: (key) => (store.has(key) ? (store.get(key) as string) : null),
    setItem: (key, value) => {
      store.set(key, String(value));
    },
    removeItem: (key) => {
      store.delete(key);
    },
    clear: () => {
      store.clear();
    },
    key: (index) => Array.from(store.keys())[index] ?? null,
    get length() {
      return store.size;
    },
  } as Storage;
};

/**
 * Returns the requested Web Storage ('local' | 'session'), falling back to an
 * in-memory no-op Storage when accessing it throws (blocked/unavailable).
 */
export const getSafeStorage = (type: 'local' | 'session'): Storage => {
  try {
    const storage =
      type === 'local' ? window.localStorage : window.sessionStorage;
    // Read-only probe: only bail out if the storage object is truly
    // inaccessible. Don't probe with a write — storage that reads but blocks
    // writes (quota full, private mode) should still be returned so existing
    // searches stay readable; saveRecentSearches already guards writes.
    storage.getItem('__recent_searches_probe__');
    return storage;
  } catch {
    return createNoopStorage();
  }
};

const readStoredEntries = (storage: Storage): unknown[] => {
  try {
    const stored = storage.getItem(RECENT_SEARCHES_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const searchKey = (options: Option[]): string =>
  options
    .map(({ value }) => value)
    .sort((a, b) => a.localeCompare(b))
    .join('|');

const OPTION_CATEGORIES: Option['category'][] = ['Suburb', 'Agency', 'Agent'];

const isFilledString = (field: unknown): field is string =>
  typeof field === 'string' && field.length > 0;

const isUsableOption = (option: unknown): option is Option => {
  const { value, label, category } = (option ?? {}) as Partial<Option>;

  const hasLabelToShow = isFilledString(label);
  const hasSlugToSearch = isFilledString(value) && !value.includes(',');
  const hasCategoryWeRecognise =
    !!category && OPTION_CATEGORIES.includes(category);

  return hasLabelToShow && hasSlugToSearch && hasCategoryWeRecognise;
};

// normalise both autocomplete + suggested suburbs inputs. The attribution
// fields are persisted so a recent search reports the same agent, agency and
// location as the search that created it. They are spread conditionally to
// leave entries stored before they were kept byte-identical on rewrite.
const toStorableOption = ({
  category,
  label,
  value,
  url,
  redirectToURL,
  contactId,
  agencyId,
  agencyName,
  suburbName,
  state,
  postcode,
}: Option): Option => ({
  category,
  label,
  value,
  ...(url ? { url } : {}),
  redirectToURL: !!redirectToURL,
  ...(contactId ? { contactId } : {}),
  ...(agencyId ? { agencyId } : {}),
  ...(agencyName ? { agencyName } : {}),
  ...(suburbName ? { suburbName } : {}),
  ...(state ? { state } : {}),
  ...(postcode ? { postcode } : {}),
});

export const getRecentSearches = (storage: Storage): RecentSearch[] => {
  const stored = readStoredEntries(storage);

  const deduped = new Map<string, RecentSearch>();

  stored.forEach((entry) => {
    const options = (
      Array.isArray((entry as RecentSearch)?.options)
        ? (entry as RecentSearch).options
        : [entry as Option]
    )
      .filter(isUsableOption)
      .map(toStorableOption);

    if (options.length === 0) {
      return;
    }

    const key = searchKey(options);
    deduped.delete(key);
    deduped.set(key, { options });
  });

  const searches = [...deduped.values()];
  const serialised = JSON.stringify(searches);

  if (serialised !== JSON.stringify(stored)) {
    try {
      storage.setItem(RECENT_SEARCHES_KEY, serialised);
    } catch {
      // Storage may be read-only (quota exceeded, private mode). The pruned
      // list is still correct for this session.
    }
  }

  return searches;
};

export const saveRecentSearches = (
  storage: Storage,
  existing: RecentSearch[],
  selected: Option[],
): RecentSearch[] => {
  if (selected.length === 0) {
    return existing;
  }

  const key = searchKey(selected);
  const capped = existing
    .filter((search) => searchKey(search.options) !== key)
    .concat({ options: selected.map(toStorableOption) })
    .slice(-MAX_RECENT_SEARCHES);

  try {
    storage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(capped));
  } catch {
    // Persistence can fail (quota exceeded, Safari private mode, storage
    // blocked); the search flow should still proceed with the in-memory list.
  }

  return capped;
};

export const toRecentSearchOption = ({ options }: RecentSearch): Option => {
  const [first] = options;

  if (options.length === 1) {
    return first.category === 'Suburb'
      ? { ...first, label: first.label.split(',')[0].trim() }
      : first;
  }

  return {
    category: first.category,
    label: options.map(({ label }) => label.split(',')[0].trim()).join(', '),
    value: options.map(({ value }) => value).join(','),
    redirectToURL: false,
  };
};
